import api from './api';

export interface Invoice {
  id: string;
  numero: string;
  entidade: string;
  entidadeId: string;
  mes: string;
  ano: number;
  montante: number;
  total: number;
  estado: 'PAGA' | 'PENDENTE';
  emitidoEm: string;
}

export interface InvoiceDetail extends Invoice {
  referencia: string;
  cliente: string;
  clienteId: string;
  servico: string;
  items: InvoiceItem[];
  pagamentos: InvoicePayment[];
}

export interface InvoiceItem {
  id: string;
  descricao: string;
  quantidade: number;
  valorUnitario: number;
  valorTotal: number;
}

export interface InvoicePayment {
  id: string;
  referencia: string;
  valor: number;
  estado: string;
  dataPagamento: string;
}

export interface InvoiceStats {
  total: number;
  pagas: number;
  pendentes: number;
  valorTotal: number;
}

export interface InvoiceFilters {
  page?: number;
  limit?: number;
  status?: string;
  year?: number;
  month?: number;
  search?: string;
  clienteId?: string;
}

export interface ClientBilling {
  id: string;
  nome: string;
  codigo: string;
  email: string;
  totalFaturas: number;
  valorPago: number;
  valorPendente: number;
  entidades: string;
  servicos: string;
  tarifarios: string;
}

export interface ClientsBillingStats {
  totalClientes: number;
  valorPago: number;
  valorPendente: number;
}

export interface ClientsBillingResponse {
  success: boolean;
  data: ClientBilling[];
  stats: ClientsBillingStats;
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
}

export interface InvoicesResponse {
  success: boolean;
  data: Invoice[];
  stats: InvoiceStats;
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
}

class BillingService {
  /**
   * Get all invoices with filters
   */
  async getInvoices(filters?: InvoiceFilters): Promise<InvoicesResponse> {
    try {
      const params = new URLSearchParams();
      if (filters?.page) params.append('page', String(filters.page));
      if (filters?.limit) params.append('limit', String(filters.limit));
      if (filters?.status) params.append('status', filters.status);
      if (filters?.year) params.append('year', String(filters.year));
      if (filters?.month) params.append('month', String(filters.month));
      if (filters?.search) params.append('search', filters.search);
      if (filters?.clienteId) params.append('clienteId', filters.clienteId);

      const response = await api.get(`/admin/invoices?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get invoices error:', error);
      throw error;
    }
  }

  /**
   * Get invoice by ID
   */
  async getInvoice(id: string): Promise<{ success: boolean; data: InvoiceDetail }> {
    try {
      const response = await api.get(`/admin/invoices/${id}`);
      return response.data;
    } catch (error) {
      console.error('Get invoice error:', error);
      throw error;
    }
  }

  /**
   * Get invoice statistics
   */
  async getStats(period?: number): Promise<{
    success: boolean;
    data: {
      stats: InvoiceStats & { valorPago: number; valorPendente: number };
      monthlyInvoices: { month: string; count: number; amount: number }[];
    };
  }> {
    try {
      const params = period ? `?period=${period}` : '';
      const response = await api.get(`/admin/invoices/stats${params}`);
      return response.data;
    } catch (error) {
      console.error('Get invoice stats error:', error);
      throw error;
    }
  }

  /**
   * Get clients with billing summary
   */
  async getClientsBilling(filters?: { search?: string; page?: number; limit?: number }): Promise<ClientsBillingResponse> {
    try {
      const params = new URLSearchParams();
      if (filters?.page) params.append('page', String(filters.page));
      if (filters?.limit) params.append('limit', String(filters.limit));
      if (filters?.search) params.append('search', filters.search);
      const response = await api.get(`/admin/invoices/clients?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get clients billing error:', error);
      throw error;
    }
  }

  /**
   * Export invoices to CSV
   */
  async exportInvoices(filters?: InvoiceFilters): Promise<Blob> {
    try {
      const params = new URLSearchParams();
      if (filters?.status) params.append('status', filters.status);
      if (filters?.year) params.append('year', String(filters.year));
      if (filters?.month) params.append('month', String(filters.month));
      if (filters?.search) params.append('search', filters.search);

      const response = await api.get(`/admin/invoices/export?${params.toString()}`, {
        responseType: 'blob',
      });

      return response.data;
    } catch (error) {
      console.error('Export invoices error:', error);
      throw error;
    }
  }
}

export default new BillingService();
